<template>
  <b-overlay :show="loading" variant="transparent" opacity="0.99" blur="5px" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <b-icon-controller font-scale="3" animation="cylon" />
        <p>กรุณารอสักครู่...</p>
      </div>
    </template>

    <div class="card mb-1">
      <div class="card-body d-flex align-items-center justify-content-between">
        <div>
          <h4 class="mb-0"><span class="text-muted">AutoPeer Direct /</span> <strong>Credit Agent</strong></h4>
        </div>
        <div class="d-flex align-items-center">
          <b-button variant="danger" class="mr-2" @click="showWithdrawModal = true">
            <feather-icon icon="ArrowDownCircleIcon" class="mr-50" /> ถอนเครดิต Agent
          </b-button>
          <b-button variant="outline-primary" @click="fetchCredit">
            <feather-icon icon="RefreshCwIcon" /> Refresh
          </b-button>
          <b-form-checkbox v-model="autoRefresh" switch class="ml-2">Auto Refresh</b-form-checkbox>
        </div>
      </div>
    </div>

    <div class="row mb-1">
      <div class="col-md-4">
        <b-card class="text-center">
          <div class="text-muted mb-half">ยอดคงเหลือ (Balance)</div>
          <h2 class="text-primary mb-0">{{ formatNum(credit.balance) }}</h2>
        </b-card>
      </div>
      <div class="col-md-4">
        <b-card class="text-center">
          <div class="text-muted mb-half">ฝากสะสม (Total Deposit)</div>
          <h2 class="text-success mb-0">{{ formatNum(credit.totalDeposit) }}</h2>
        </b-card>
      </div>
      <div class="col-md-4">
        <b-card class="text-center">
          <div class="text-muted mb-half">ถอนสะสม (Total Withdraw)</div>
          <h2 class="text-danger mb-0">{{ formatNum(credit.totalWithdraw) }}</h2>
        </b-card>
      </div>
    </div>

    <!-- รายการถอนเครดิต Agent -->
    <b-card>
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h5 class="mb-0">รายการถอนเครดิต Agent</h5>
        <b-button size="sm" variant="outline-primary" @click="fetchWithdrawalsCredit">
          <feather-icon icon="RefreshCwIcon" size="14" /> รีเฟรช
        </b-button>
      </div>
      <div class="row mb-2">
        <b-col md="2">
          <label>สถานะ</label>
          <b-form-select v-model="withdrawalsFilters.status" :options="statusOptions" size="sm" @change="fetchWithdrawalsCredit" />
        </b-col>
        <b-col md="3">
          <label>วันที่เริ่มต้น</label>
          <flat-pickr
            v-model="withdrawalsFilters.startDate"
            class="form-control form-control-sm"
            :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: true }"
          />
        </b-col>
        <b-col md="3">
          <label>วันที่สิ้นสุด</label>
          <flat-pickr
            v-model="withdrawalsFilters.endDate"
            class="form-control form-control-sm"
            :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: true }"
          />
        </b-col>
        <b-col md="2" class="d-flex align-items-end">
          <b-button size="sm" variant="gradient-dark" @click="fetchWithdrawalsCredit">
            <feather-icon icon="SearchIcon" size="14" /> ค้นหา
          </b-button>
        </b-col>
      </div>
      <b-table
        small
        striped
        hover
        responsive
        show-empty
        :items="withdrawalsList"
        :fields="withdrawalsFields"
        :per-page="withdrawalsPerPage"
        class="position-relative"
      >
        <template #cell(index)="data">
          {{ withdrawalsPerPage * (withdrawalsPage - 1) + (data.index + 1) }}
        </template>
        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>
        <template #cell(amount)="data">
          {{ formatNum(data.item.amount) }}
        </template>
        <template #cell(actualAmount)="data">
          {{ data.item.actualAmount != null ? formatNum(data.item.actualAmount) : '-' }}
        </template>
        <template #cell(status)="data">
          <span class="badge" :class="statusClass(data.item.status)">{{ statusLabel(data.item.status) }}</span>
          <small v-if="data.item.retryCount > 0 && data.item.status === 'processing'" class="d-block text-muted mt-25">
            retry {{ data.item.retryCount }}/3
          </small>
        </template>
        <template #cell(bankInfo)="data">
          {{ bankLabel(data.item.bankCode) }}
        </template>
        <template #cell(accountNumber)="data">
          {{ data.item.accountNumber || '-' }}
        </template>
        <template #cell(recipientName)="data">
          {{ [data.item.firstName, data.item.lastName].filter(Boolean).join(' ') || '-' }}
        </template>
        <template #cell(remark)="data">
          <small v-if="data.item.remark" class="text-muted">{{ data.item.remark }}</small>
          <span v-else>-</span>
        </template>
        <template #cell(actions)="data">
          <div class="d-flex align-items-center" style="gap: 0.4rem;">
            <b-button
              v-if="showResetButton(data.item)"
              size="sm"
              variant="warning"
              :disabled="resettingId === String(data.item._id)"
              @click="resetWithdrawCredit(data.item)"
            >
              <b-spinner v-if="resettingId === String(data.item._id)" small class="mr-50" />
              {{ resetButtonLabel(data.item) }}
            </b-button>
            <b-button
              v-if="showCancelButton(data.item)"
              size="sm"
              variant="outline-danger"
              :disabled="cancellingId === String(data.item._id)"
              @click="cancelWithdrawCredit(data.item)"
            >
              <b-spinner v-if="cancellingId === String(data.item._id)" small class="mr-50" />
              ยกเลิก
            </b-button>
          </div>
        </template>
        <template #empty>
          <div class="p-2 text-center">
            <img src="/box.png" alt="empty" height="40" /> ไม่พบรายการถอนเครดิต Agent
          </div>
        </template>
      </b-table>
      <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
        <b-form-group label="Per Page" label-cols="8" label-align="left" label-size="sm" class="text-nowrap mb-md-0 mr-1">
          <b-form-select v-model="withdrawalsPerPage" size="sm" :options="[10, 20, 50, 100]" @change="fetchWithdrawalsCredit" />
        </b-form-group>
        <b-pagination
          v-model="withdrawalsPage"
          :total-rows="withdrawalsTotal"
          :per-page="withdrawalsPerPage"
          first-number
          last-number
          class="mb-0"
          @input="fetchWithdrawalsCredit"
        >
          <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
          <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
        </b-pagination>
      </b-card-body>
    </b-card>

    <!-- Modal ถอนเครดิต Agent -->
    <b-modal
      v-model="showWithdrawModal"
      title="ถอนเครดิต Agent"
      centered
      no-close-on-backdrop
      hide-footer
      size="lg"
    >
      <div class="mb-3 p-2 rounded" style="background: #f8f9fa;">
        <div class="d-flex justify-content-between">
          <span class="text-muted">ยอดคงเหลือ</span>
          <strong class="text-primary">{{ formatNum(credit.balance) }} บาท</strong>
        </div>
      </div>
      <b-form @submit.prevent="submitWithdrawCredit">
        <b-row>
          <b-col md="6">
            <b-form-group label="ยอดถอน (บาท)" label-for="modal-wd-amount">
              <b-form-input
                id="modal-wd-amount"
                v-model.number="withdrawForm.amount"
                type="number"
                min="1"
                step="1"
                placeholder="ระบุยอดที่ต้องการถอน"
                :disabled="submitting"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="ธนาคาร" label-for="modal-wd-bank">
              <b-form-select
                id="modal-wd-bank"
                v-model="withdrawForm.bankCode"
                :options="bankOptions"
                :disabled="submitting"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="เลขบัญชีรับโอน" label-for="modal-wd-acc">
              <b-form-input
                id="modal-wd-acc"
                v-model="withdrawForm.accountNumber"
                type="text"
                placeholder="เลขบัญชี"
                :disabled="submitting"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="ชื่อผู้รับ" label-for="modal-wd-first">
              <b-form-input
                id="modal-wd-first"
                v-model="withdrawForm.firstName"
                type="text"
                placeholder="ชื่อ (ภาษาไทย)"
                :disabled="submitting"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="นามสกุลผู้รับ" label-for="modal-wd-last">
              <b-form-input
                id="modal-wd-last"
                v-model="withdrawForm.lastName"
                type="text"
                placeholder="นามสกุล"
                :disabled="submitting"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-form-group label="หมายเหตุ (ไม่บังคับ)" label-for="modal-wd-remarks">
              <b-form-input
                id="modal-wd-remarks"
                v-model="withdrawForm.remarks"
                type="text"
                placeholder="หมายเหตุ"
                :disabled="submitting"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <hr />
        <div class="d-flex justify-content-end">
          <b-button variant="outline-secondary" class="mr-1" :disabled="submitting" @click="showWithdrawModal = false">
            ยกเลิก
          </b-button>
          <b-button type="submit" variant="danger" :disabled="submitting">
            <b-spinner v-if="submitting" small class="mr-50" />
            ยืนยันถอนเครดิต
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </b-overlay>
</template>

<script>
import moment from 'moment-timezone'
import flatPickr from 'vue-flatpickr-component'
import {
  BOverlay,
  BIconController,
  BCard,
  BFormCheckbox,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BSpinner,
  BRow,
  BCol,
  BTable,
  BCardBody,
  BPagination,
  BModal,
} from 'bootstrap-vue'

const BANK_LIST = [
  { code: '004', name: 'KBANK - ธนาคารกสิกรไทย' },
  { code: '014', name: 'SCB - ธนาคารไทยพาณิชย์' },
  { code: '002', name: 'BBL - ธนาคารกรุงเทพ' },
  { code: '006', name: 'KTB - ธนาคารกรุงไทย' },
  { code: '011', name: 'TTB - ธนาคารทหารไทยธนชาต' },
  { code: '025', name: 'BAY - ธนาคารกรุงศรีอยุธยา' },
  { code: '030', name: 'GSB - ธนาคารออมสิน' },
  { code: '033', name: 'GHB - ธนาคารอาคารสงเคราะห์' },
  { code: '034', name: 'BAAC - ธนาคารเพื่อการเกษตรฯ' },
  { code: '022', name: 'CIMB - ธนาคารซีไอเอ็มบี' },
  { code: '024', name: 'UOB - ธนาคารยูโอบี' },
  { code: '069', name: 'KKP - ธนาคารเกียรตินาคินภัทร' },
  { code: '067', name: 'TISCO - ธนาคารทิสโก้' },
  { code: '066', name: 'ISBT - ธนาคารอิสลาม' },
]

const BANK_CODE_NAME = {}
BANK_LIST.forEach((b) => { BANK_CODE_NAME[b.code] = b.name })

export default {
  components: {
    flatPickr,
    BOverlay,
    BIconController,
    BCard,
    BFormCheckbox,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BSpinner,
    BRow,
    BCol,
    BTable,
    BCardBody,
    BPagination,
    BModal,
  },
  data() {
    return {
      loading: false,
      autoRefresh: false,
      refreshInterval: null,
      credit: { balance: 0, totalDeposit: 0, totalWithdraw: 0, lastUpdatedAt: null },
      showWithdrawModal: false,
      submitting: false,
      resettingId: null,
      cancellingId: null,
      withdrawForm: {
        amount: null,
        bankCode: '',
        accountNumber: '',
        firstName: '',
        lastName: '',
        remarks: '',
      },
      bankOptions: [{ value: '', text: '-- เลือกธนาคาร --' }].concat(
        BANK_LIST.map((b) => ({ value: b.code, text: b.name }))
      ),
      withdrawalsList: [],
      withdrawalsTotal: 0,
      withdrawalsPage: 1,
      withdrawalsPerPage: 20,
      withdrawalsFilters: {
        status: '',
        startDate: moment().tz('Asia/Bangkok').format('YYYY-MM-DD 00:00'),
        endDate: moment().tz('Asia/Bangkok').format('YYYY-MM-DD 23:59'),
      },
      statusOptions: [
        { value: '', text: 'ทั้งหมด' },
        { value: 'processing', text: 'กำลังดำเนินการ' },
        { value: 'success', text: 'สำเร็จ' },
        { value: 'cancelled', text: 'ยกเลิก' },
        { value: 'error', text: 'ไม่สำเร็จ' },
        { value: 'waiting_approve', text: 'รอถอนต่อ' },
      ],
      withdrawalsFields: [
        { key: 'index', label: 'No.' },
        { key: 'created_at', label: 'วันที่' },
        { key: 'amount', label: 'ยอดขอถอน' },
        { key: 'actualAmount', label: 'ยอดถอนจริง' },
        { key: 'status', label: 'สถานะ' },
        { key: 'remark', label: 'หมายเหตุ' },
        { key: 'refNo', label: 'Ref No' },
        { key: 'bankInfo', label: 'ธนาคาร' },
        { key: 'accountNumber', label: 'เลขบัญชี' },
        { key: 'recipientName', label: 'ชื่อผู้รับ' },
        { key: 'actions', label: '' },
      ],
    }
  },
  created() {
    this.refreshInterval = setInterval(() => {
      if (this.autoRefresh) {
        this.fetchCredit()
        this.fetchWithdrawalsCredit()
      }
    }, 30000)
  },
  destroyed() {
    if (this.refreshInterval) clearInterval(this.refreshInterval)
  },
  mounted() {
    this.fetchCredit()
    this.fetchWithdrawalsCredit()
  },
  methods: {
    async fetchCredit() {
      this.loading = true
      try {
        const { data: res } = await this.$http.get('/autopeer-direct/credit')
        const d = res.data || res
        this.credit.balance = Number(d.balance || 0)
        this.credit.totalDeposit = Number(d.totalDeposit || 0)
        this.credit.totalWithdraw = Number(d.totalWithdraw || 0)
        this.credit.lastUpdatedAt = d.lastUpdatedAt || null
      } catch (err) {
        console.error(err)
        this.$swal({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถโหลดข้อมูลเครดิตได้' })
      } finally {
        this.loading = false
      }
    },
    async submitWithdrawCredit() {
      const { amount, bankCode, accountNumber, firstName, lastName } = this.withdrawForm
      if (!amount || amount < 1) {
        this.$swal({ icon: 'warning', title: 'ข้อมูลไม่ครบ', text: 'กรุณาระบุยอดถอน' })
        return
      }
      if (!bankCode || !accountNumber || !firstName || !lastName) {
        this.$swal({ icon: 'warning', title: 'ข้อมูลไม่ครบ', text: 'กรุณาระบุ ธนาคาร เลขบัญชี ชื่อ และนามสกุลผู้รับ' })
        return
      }
      const confirm = await this.$swal({
        icon: 'question',
        title: 'ยืนยันถอนเครดิต Agent?',
        html: `<div class="text-center">ยอด <strong>${Number(amount).toLocaleString()}</strong> บาท<br>บัญชี ${accountNumber}<br>${firstName} ${lastName}</div>`,
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
        customClass: {
          confirmButton: 'btn btn-primary btn-agent-credit-confirm',
          cancelButton: 'btn btn-secondary btn-agent-credit-cancel',
          actions: 'swal2-actions-agent-credit',
        },
      })
      if (!confirm.isConfirmed) return
      this.submitting = true
      try {
        const { data: res } = await this.$http.post('/autopeer-direct/withdraw-credit', {
          amount: Number(amount),
          bankCode,
          accountNumber: String(accountNumber).trim(),
          firstName: String(firstName).trim(),
          lastName: String(lastName).trim(),
          remarks: this.withdrawForm.remarks ? String(this.withdrawForm.remarks).trim() : undefined,
        })
        const d = res.data || res
        this.$swal({
          icon: 'success',
          title: 'ส่งคำขอถอนเครดิต Agent สำเร็จ',
          text: d.refNo ? `Ref: ${d.refNo}` : 'ระบบกำลังดำเนินการ',
        })
        this.withdrawForm.amount = null
        this.withdrawForm.accountNumber = ''
        this.withdrawForm.firstName = ''
        this.withdrawForm.lastName = ''
        this.withdrawForm.remarks = ''
        this.showWithdrawModal = false
        await this.fetchCredit()
        await this.fetchWithdrawalsCredit()
      } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'เกิดข้อผิดพลาด'
        const text = err.response?.data?.message === 'insufficient_balance' && err.response?.data?.data
          ? `ยอดคงเหลือไม่พอ (คงเหลือ: ${Number(err.response.data.data.balance || 0).toLocaleString()})`
          : msg
        this.$swal({ icon: 'error', title: 'ถอนเครดิต Agent ไม่สำเร็จ', text })
      } finally {
        this.submitting = false
      }
    },
    async fetchWithdrawalsCredit() {
      this.loading = true
      try {
        const { data: res } = await this.$http.get('/autopeer-direct/withdrawals-credit', {
          params: {
            page: this.withdrawalsPage,
            limit: this.withdrawalsPerPage,
            status: this.withdrawalsFilters.status || undefined,
            startDate: this.withdrawalsFilters.startDate ? moment(this.withdrawalsFilters.startDate).toISOString() : undefined,
            endDate: this.withdrawalsFilters.endDate ? moment(this.withdrawalsFilters.endDate).toISOString() : undefined,
          },
        })
        const d = res.data || res
        this.withdrawalsList = d.list || []
        this.withdrawalsTotal = d.pagination?.total ?? this.withdrawalsList.length
      } catch (err) {
        console.error(err)
        this.$swal({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถโหลดรายการถอนเครดิต Agent ได้' })
        this.withdrawalsList = []
      } finally {
        this.loading = false
      }
    },
    formatNum(v) {
      return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })
    },
    formatDate(v) {
      return v ? moment(v).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm:ss') : '-'
    },
    statusClass(s) {
      if (s === 'success') return 'badge-success'
      if (s === 'cancelled') return 'badge-danger'
      if (s === 'error') return 'badge-danger'
      if (s === 'waiting_approve') return 'badge-warning'
      return 'badge-info'
    },
    statusLabel(s) {
      const map = { processing: 'กำลังดำเนินการ', success: 'สำเร็จ', cancelled: 'ยกเลิก', error: 'ไม่สำเร็จ', waiting_approve: 'รอถอนต่อ' }
      return map[s] || s || '-'
    },
    isProcessingStuck(item) {
      if (item.status !== 'processing') return false
      if (item.callbackRaw != null && Object.keys(item.callbackRaw || {}).length > 0) return false
      const created = item.created_at ? new Date(item.created_at).getTime() : 0
      const now = Date.now()
      const fifteenMin = 15 * 60 * 1000
      return now - created > fifteenMin
    },
    showResetButton(item) {
      if (item.status === 'waiting_approve') return true
      if (item.status === 'error' && (Number(item.retryCount) || 0) < 3) return true
      return false
    },
    resetButtonLabel(item) {
      if (item.status === 'waiting_approve') return 'อนุมัติ'
      return 'รีเซ็ท'
    },
    showCancelButton(item) {
      if (item.status === 'error' && (Number(item.retryCount) || 0) >= 3) return false
      if (item.status === 'error') return true
      if (item.status === 'cancelled' && !(item.remark || '').includes('คืนเครดิต')) return true
      if (item.status === 'processing' && this.isProcessingStuck(item) && (Number(item.retryCount) || 0) === 0) return true
      return false
    },
    async cancelWithdrawCredit(item) {
      const confirm = await this.$swal({
        icon: 'warning',
        title: 'ยกเลิกรายการถอนเครดิต Agent?',
        html: `<div class="text-center">ยอด <strong>${Number(item.amount).toLocaleString()}</strong> บาท<br>AutoPeer จะคืนเครดิตให้ Agent ทันที</div>`,
        showCancelButton: true,
        confirmButtonText: 'ยืนยันยกเลิก',
        cancelButtonText: 'ปิด',
        customClass: {
          confirmButton: 'btn btn-danger btn-agent-credit-confirm',
          cancelButton: 'btn btn-secondary btn-agent-credit-cancel',
          actions: 'swal2-actions-agent-credit',
        },
      })
      if (!confirm.isConfirmed) return
      this.cancellingId = String(item._id)
      try {
        const { data: res } = await this.$http.post('/autopeer-direct/withdraw-credit/cancel', { id: String(item._id) })
        this.$swal({
          icon: 'success',
          title: 'ยกเลิกสำเร็จ',
          text: 'AutoPeer คืนเครดิตแล้ว',
        })
        await this.fetchWithdrawalsCredit()
        await this.fetchCredit()
      } catch (err) {
        const data = err.response?.data || {}
        const msg = data.hint || data.message || data.error || err.message || 'เกิดข้อผิดพลาด'
        this.$swal({ icon: 'error', title: 'ยกเลิกไม่สำเร็จ', text: msg })
      } finally {
        this.cancellingId = null
      }
    },
    async resetWithdrawCredit(item) {
      const isApprove = item.status === 'waiting_approve'
      const confirm = await this.$swal({
        icon: 'warning',
        title: isApprove ? 'อนุมัติรายการถอนเครดิต Agent?' : 'รีเซ็ทรายการถอนเครดิต Agent?',
        html: `<div class="text-center">ยอด <strong>${Number(item.amount).toLocaleString()}</strong> บาท<br>${isApprove ? 'จะยิง AutoPeer ถอนยอดนี้' : 'จะยิง AutoPeer ถอนใหม่'}</div>`,
        showCancelButton: true,
        confirmButtonText: isApprove ? 'ยืนยันอนุมัติ' : 'ยืนยันรีเซ็ท',
        cancelButtonText: 'ยกเลิก',
        customClass: {
          confirmButton: 'btn btn-warning btn-agent-credit-confirm',
          cancelButton: 'btn btn-secondary btn-agent-credit-cancel',
          actions: 'swal2-actions-agent-credit',
        },
      })
      if (!confirm.isConfirmed) return
      this.resettingId = String(item._id)
      try {
        await this.$http.post('/autopeer-direct/withdraw-credit/reset', { id: String(item._id) })
        this.$swal({ icon: 'success', title: isApprove ? 'อนุมัติสำเร็จ' : 'รีเซ็ทสำเร็จ', text: 'ระบบกำลังยิง AutoPeer ถอน' })
        await this.fetchWithdrawalsCredit()
        await this.fetchCredit()
      } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'เกิดข้อผิดพลาด'
        this.$swal({ icon: 'error', title: 'รีเซ็ทไม่สำเร็จ', text: msg })
      } finally {
        this.resettingId = null
      }
    },
    bankLabel(code) {
      return BANK_CODE_NAME[code] || code || '-'
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
<style lang="scss">
.swal2-popup .swal2-actions-agent-credit {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 0.5rem;
}
.swal2-popup .swal2-actions-agent-credit .swal2-confirm,
.swal2-popup .swal2-actions-agent-credit .swal2-cancel {
  min-width: 5rem;
  margin: 0;
}
</style>
